import { useState, useEffect, useRef, RefObject } from 'react';

/**
 * Options for the useInView hook
 */
interface UseInViewOptions {
  /** Root margin for IntersectionObserver (default: '100px') */
  rootMargin?: string;
  /** Threshold for visibility (default: 0) */
  threshold?: number | number[];
  /** Whether to disconnect observer after first intersection (default: false) */
  triggerOnce?: boolean;
}

/**
 * Hook to detect if an element is visible in the viewport
 * 
 * @param options - Configuration options
 * @returns [ref, isInView] - Ref to attach to element and visibility state
 */
export const useInView = <T extends HTMLElement = HTMLDivElement>(
  options: UseInViewOptions = {}
): [RefObject<T>, boolean] => {
  const { rootMargin = '100px', threshold = 0, triggerOnce = false } = options;
  
  const ref = useRef<T>(null);
  const [isInView, setIsInView] = useState(false);
  const hasTriggered = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // If already triggered and triggerOnce is true, skip
    if (triggerOnce && hasTriggered.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const inView = entry.isIntersecting;
        
        setIsInView(inView);

        if (inView && triggerOnce) {
          hasTriggered.current = true;
          observer.disconnect();
        }
      },
      {
        rootMargin,
        threshold,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, threshold, triggerOnce]);

  return [ref, isInView];
};

export default useInView;

